import { GoogleGenerativeAI } from '@google/generative-ai'

// Cache utilities
const CACHE_PREFIX = 'ai_business_targets_'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours

interface CacheEntry {
    data: RevenueInsightResponse
    timestamp: number
}

export interface RevenueInsightResponse {
    summary: string
    customer_insights: {
        new_customers: string
        retained_customers: string
        customer_analysis: string
    }
    revenue_breakdown: {
        new_sale_analysis: string
        renewal_analysis: string
        upsell_analysis: string
    }
    trends: string[]
    recommendations: string[]
    comparison_insights?: string // When comparing periods
}

export interface RevenueAnalysisRequest {
    data: any[]
    period_range: string
    comparison_data?: any[]
    comparison_period_range?: string
}

export interface ServiceBreakdownInsightResponse {
    summary: string
    top_growing_services: {
        name: string
        analysis: string
    }[]
    declining_services?: {
        name: string
        analysis: string
    }[]
    growth_drivers: string[]
    recommendations: string[]
    comparison_insights?: string
}

export interface ServiceBreakdownAnalysisRequest {
    data: any[]
    period_range: string
    comparison_data?: any[]
    comparison_period_range?: string
}

const createDataHash = (data: any[]): string => {
    if (!data || data.length === 0) return 'empty'

    const hashString = JSON.stringify({
        length: data.length,
        first: data[0]?.month,
        last: data[data.length - 1]?.month,
        totalRevenue: data.reduce(
            (sum, d) => sum + (d['Tổng doanh thu'] || 0),
            0
        ),
    })

    let hash = 0
    for (let i = 0; i < hashString.length; i++) {
        const char = hashString.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
    }

    return Math.abs(hash).toString(36)
}

const generateCacheKey = (params: {
    period_range: string
    data_hash: string
    comparison_hash?: string
}): string => {
    const { period_range, data_hash, comparison_hash } = params
    return `${CACHE_PREFIX}${period_range}_${data_hash}_${comparison_hash || 'none'}`
}

const getCachedAnalysis = (cacheKey: string): RevenueInsightResponse | null => {
    try {
        const cached = localStorage.getItem(cacheKey)
        if (!cached) return null

        const entry: CacheEntry = JSON.parse(cached)
        if (Date.now() - entry.timestamp > CACHE_DURATION) {
            localStorage.removeItem(cacheKey)
            return null
        }

        console.log('📦 Using cached revenue analysis')
        return entry.data
    } catch {
        return null
    }
}

const setCachedAnalysis = (
    cacheKey: string,
    data: RevenueInsightResponse
): void => {
    try {
        const entry: CacheEntry = {
            data,
            timestamp: Date.now(),
        }
        localStorage.setItem(cacheKey, JSON.stringify(entry))
        console.log('💾 Revenue analysis cached')
    } catch (error) {
        console.error('Error caching analysis:', error)
    }
}

const initializeGemini = () => {
    const apiKey = import.meta.env.APP_GEMINI_API_KEY
    if (!apiKey) {
        throw new Error('Gemini API key not found')
    }

    const genAI = new GoogleGenerativeAI(apiKey)
    return genAI.getGenerativeModel({
        model: 'gemini-2.0-flash-lite',
        generationConfig: {
            responseMimeType: 'application/json',
        },
    })
}

export const analyzeRevenueInsights = async (
    request: RevenueAnalysisRequest
): Promise<RevenueInsightResponse> => {
    const dataHash = createDataHash(request.data)
    const comparisonHash = request.comparison_data
        ? createDataHash(request.comparison_data)
        : undefined
    const cacheKey = generateCacheKey({
        period_range: request.period_range,
        data_hash: dataHash,
        comparison_hash: comparisonHash,
    })

    const cached = getCachedAnalysis(cacheKey)
    if (cached) return cached

    console.log('🔄 Fetching revenue insights from Gemini...')

    const formatCurrency = (num: number) =>
        new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(num)

    // Calculate totals
    const totals = {
        total_revenue: request.data.reduce(
            (sum, d) => sum + (d['Tổng doanh thu'] || 0),
            0
        ),
        renewal: request.data.reduce((sum, d) => sum + (d['Duy trì'] || 0), 0),
        new_sale: request.data.reduce((sum, d) => sum + (d['Bán mới'] || 0), 0),
        upsell: request.data.reduce((sum, d) => sum + (d.Upsell || 0), 0),
    }

    const dataSummary = request.data.map((item) => ({
        month: item.month,
        total_revenue: formatCurrency(item['Tổng doanh thu'] || 0),
        renewal: formatCurrency(item['Duy trì'] || 0),
        new_sale: formatCurrency(item['Bán mới'] || 0),
        upsell: formatCurrency(item.Upsell || 0),
    }))

    let comparisonSection = ''
    if (request.comparison_data && request.comparison_period_range) {
        const compTotals = {
            total_revenue: request.comparison_data.reduce(
                (sum, d) => sum + (d['Tổng doanh thu'] || 0),
                0
            ),
            renewal: request.comparison_data.reduce(
                (sum, d) => sum + (d['Duy trì'] || 0),
                0
            ),
            new_sale: request.comparison_data.reduce(
                (sum, d) => sum + (d['Bán mới'] || 0),
                0
            ),
            upsell: request.comparison_data.reduce(
                (sum, d) => sum + (d.Upsell || 0),
                0
            ),
        }

        comparisonSection = `
        DỮ LIỆU KỲ SO SÁNH (${request.comparison_period_range}):
        - Tổng doanh thu: ${formatCurrency(compTotals.total_revenue)}
        - Doanh thu duy trì: ${formatCurrency(compTotals.renewal)}
        - Doanh thu bán mới: ${formatCurrency(compTotals.new_sale)}
        - Doanh thu upsell: ${formatCurrency(compTotals.upsell)}

        THAY ĐỔI SO VỚI KỲ TRƯỚC:
        - Tổng doanh thu: ${(((totals.total_revenue - compTotals.total_revenue) / compTotals.total_revenue) * 100).toFixed(1)}%
        - Duy trì: ${(((totals.renewal - compTotals.renewal) / compTotals.renewal) * 100).toFixed(1)}%
        - Bán mới: ${(((totals.new_sale - compTotals.new_sale) / compTotals.new_sale) * 100).toFixed(1)}%
        - Upsell: ${(((totals.upsell - compTotals.upsell) / compTotals.upsell) * 100).toFixed(1)}%
        `
    }

    const prompt = `
    Bạn là chuyên gia phân tích kinh doanh với nhiều năm kinh nghiệm trong lĩnh vực sales và customer success.

    NHIỆM VỤ:
    Phân tích dữ liệu doanh thu và đưa ra insights về nguồn khách hàng, xu hướng tăng trưởng.

    DỮ LIỆU DOANH THU (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    TỔNG KẾT:
    - Tổng doanh thu: ${formatCurrency(totals.total_revenue)}
    - Doanh thu duy trì (từ KH cũ): ${formatCurrency(totals.renewal)} (${((totals.renewal / totals.total_revenue) * 100).toFixed(1)}%)
    - Doanh thu bán mới (từ KH mới): ${formatCurrency(totals.new_sale)} (${((totals.new_sale / totals.total_revenue) * 100).toFixed(1)}%)
    - Doanh thu upsell: ${formatCurrency(totals.upsell)} (${((totals.upsell / totals.total_revenue) * 100).toFixed(1)}%)

    ${comparisonSection}

    YÊU CẦU PHÂN TÍCH:

    1. **Summary**: Tóm tắt ngắn gọn (2-3 câu) về tình hình doanh thu

    2. **Customer Insights**: Phân tích về nguồn khách hàng
       - new_customers: Nhận xét về doanh thu từ khách hàng mới (bán mới)
       - retained_customers: Nhận xét về doanh thu từ khách hàng cũ (duy trì + upsell)
       - customer_analysis: Phân tích tổng quan về cơ cấu khách hàng, tỷ lệ retention

    3. **Revenue Breakdown**: Phân tích từng loại doanh thu
       - new_sale_analysis: Phân tích doanh thu bán mới - xu hướng, tháng cao/thấp
       - renewal_analysis: Phân tích doanh thu duy trì - mức độ ổn định, retention rate
       - upsell_analysis: Phân tích doanh thu upsell - tiềm năng cross-sell/upsell

    4. **Trends**: 3-5 xu hướng quan trọng nhất từ dữ liệu

    5. **Recommendations**: 3-5 đề xuất hành động cụ thể để cải thiện doanh thu

    ${request.comparison_data ? '6. **Comparison Insights**: Phân tích so sánh giữa 2 kỳ, highlight những thay đổi quan trọng' : ''}

    Trả về JSON với format:
    {
        "summary": "Tóm tắt...",
        "customer_insights": {
            "new_customers": "...",
            "retained_customers": "...",
            "customer_analysis": "..."
        },
        "revenue_breakdown": {
            "new_sale_analysis": "...",
            "renewal_analysis": "...",
            "upsell_analysis": "..."
        },
        "trends": ["Trend 1", "Trend 2"],
        "recommendations": ["Action 1", "Action 2"]${request.comparison_data ? ',\n        "comparison_insights": "..."' : ''}
    }

    LƯU Ý:
    - Sử dụng tiếng Việt
    - Đưa ra số liệu cụ thể khi phân tích
    - Tập trung vào actionable insights
    - Phân tích mối quan hệ giữa KH mới và KH cũ
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeServiceBreakdownInsights = async (
    request: ServiceBreakdownAnalysisRequest
): Promise<ServiceBreakdownInsightResponse> => {
    const dataHash = createDataHash(request.data)
    const comparisonHash = request.comparison_data
        ? createDataHash(request.comparison_data)
        : undefined
    const cacheKey = `${CACHE_PREFIX}service_${request.period_range}_${dataHash}_${comparisonHash || 'none'}`

    const cached = getCachedAnalysis(cacheKey)
    if (cached) return cached as unknown as ServiceBreakdownInsightResponse

    console.log('🔄 Fetching service breakdown insights from Gemini...')

    const formatCurrency = (num: number) =>
        new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(num)

    // Calculate totals and changes
    const serviceAnalysis = request.data.map((service: any) => {
        const change =
            service.compTotal > 0
                ? (
                      ((service.total - service.compTotal) /
                          service.compTotal) *
                      100
                  ).toFixed(1)
                : 'N/A'

        return {
            name: service.name,
            total: formatCurrency(service.total),
            renewal: formatCurrency(service.renewal),
            newSale: formatCurrency(service.newSale),
            upsell: formatCurrency(service.upsell),
            change: change,
            compTotal: service.compTotal
                ? formatCurrency(service.compTotal)
                : 'N/A',
        }
    })

    const totalRevenue = request.data.reduce(
        (sum: number, s: any) => sum + s.total,
        0
    )
    const totalRenewal = request.data.reduce(
        (sum: number, s: any) => sum + s.renewal,
        0
    )
    const totalNewSale = request.data.reduce(
        (sum: number, s: any) => sum + s.newSale,
        0
    )
    const totalUpsell = request.data.reduce(
        (sum: number, s: any) => sum + s.upsell,
        0
    )

    let comparisonSection = ''
    if (request.comparison_data && request.comparison_period_range) {
        const compTotal = request.comparison_data.reduce(
            (sum: number, s: any) => sum + s.compTotal,
            0
        )
        const change =
            compTotal > 0
                ? (((totalRevenue - compTotal) / compTotal) * 100).toFixed(1)
                : 'N/A'

        comparisonSection = `
        SO SÁNH VỚI KỲ TRƯỚC (${request.comparison_period_range}):
        - Tổng doanh thu kỳ trước: ${formatCurrency(compTotal)}
        - Thay đổi: ${change}%
        `
    }

    const prompt = `
    Bạn là chuyên gia phân tích kinh doanh với nhiều năm kinh nghiệm trong lĩnh vực dịch vụ công nghệ.

    NHIỆM VỤ:
    Phân tích dữ liệu doanh thu theo dịch vụ và đưa ra insights về tăng trưởng, nguyên nhân tăng/giảm.

    DỮ LIỆU DOANH THU THEO DỊCH VỤ (${request.period_range}):
    ${JSON.stringify(serviceAnalysis, null, 2)}

    TỔNG KẾT:
    - Tổng doanh thu: ${formatCurrency(totalRevenue)}
    - Doanh thu duy trì: ${formatCurrency(totalRenewal)} (${((totalRenewal / totalRevenue) * 100).toFixed(1)}%)
    - Doanh thu bán mới: ${formatCurrency(totalNewSale)} (${((totalNewSale / totalRevenue) * 100).toFixed(1)}%)
    - Doanh thu upsell: ${formatCurrency(totalUpsell)} (${((totalUpsell / totalRevenue) * 100).toFixed(1)}%)

    ${comparisonSection}

    YÊU CẦU PHÂN TÍCH:

    1. **Summary**: Tóm tắt ngắn gọn (2-3 câu) về tình hình doanh thu theo dịch vụ

    2. **Top Growing Services**: Phân tích 2-3 dịch vụ tăng trưởng mạnh nhất
       - Lý do tăng (bán mới nhiều? khách gia hạn dùng nhiều hơn? upsell thành công?)
       - Đánh giá tiềm năng tiếp tục tăng trưởng

    3. **Declining Services**: Phân tích dịch vụ giảm (nếu có)
       - Lý do giảm (mất khách? khách giảm sử dụng? cạnh tranh?)
       - Đề xuất cải thiện

    4. **Growth Drivers**: 3-5 động lực chính thúc đẩy tăng trưởng doanh thu

    5. **Recommendations**: 3-5 đề xuất hành động cụ thể

    ${request.comparison_data ? '6. **Comparison Insights**: So sánh chi tiết giữa 2 kỳ, highlight dịch vụ có thay đổi đáng kể' : ''}

    Trả về JSON với format:
    {
        "summary": "Tóm tắt...",
        "top_growing_services": [
            { "name": "Tên dịch vụ", "analysis": "Phân tích lý do tăng..." }
        ],
        "declining_services": [
            { "name": "Tên dịch vụ", "analysis": "Phân tích lý do giảm..." }
        ],
        "growth_drivers": ["Driver 1", "Driver 2"],
        "recommendations": ["Action 1", "Action 2"]${request.comparison_data ? ',\n        "comparison_insights": "..."' : ''}
    }

    LƯU Ý:
    - Sử dụng tiếng Việt
    - Phân tích cụ thể từng dịch vụ với số liệu
    - Giải thích rõ nguyên nhân tăng/giảm (do bán mới, duy trì hay upsell)
    - Tập trung vào actionable insights
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini service analysis failed:', error)
        throw error
    }
}
